import React, { useEffect, useMemo, useState } from 'react';
import { Circle, CircleDot, PlusCircle } from 'lucide-react';

import { BottomSheet, Spinner } from './primitives';
import { Colors } from './theme';

/**
 * What the load goes out on, and with whom.
 *
 * Two questions, both answered on one sheet without leaving it. The desktop
 * Dispatch page asks them in a rail beside the list; a phone has no rail, and
 * pushing them onto separate screens would make booking a van a four-screen
 * errand at the end of a shift.
 *
 * "Ours" is the list on file; "Hired" is a van or a man turning up at the gate,
 * typed in and used once. Adding to the list happens inside the picker rather
 * than on a settings screen, because that is where the supervisor is standing
 * when they find the driver is not on it.
 *
 * Mirrors `mobile-frontend/src/components/DispatchLoadSheet.tsx`.
 */

const isMobileNumber = (value) => /^\d{10}$/.test(String(value || '').replace(/\D/g, ''));

function Field({ value, onChange, placeholder, testID, ...rest }) {
  return (
    <input
      data-testid={testID}
      className="h-12 w-full rounded-[10px] border px-4 text-[13px] outline-none"
      style={{
        backgroundColor: Colors.background.secondary,
        borderColor: Colors.border.default,
        color: Colors.text.primary,
      }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={placeholder}
      {...rest}
    />
  );
}

function ModeToggle({ mode, onChange, testID }) {
  return (
    <div className="flex gap-1.5">
      {['ours', 'hired'].map((option) => {
        const on = mode === option;
        return (
          <button
            key={option}
            type="button"
            data-testid={`${testID}-${option}`}
            aria-pressed={on}
            onClick={() => onChange(option)}
            className="rounded-full border px-3 py-1 text-[11px] font-semibold"
            style={{
              backgroundColor: on ? Colors.accent.DEFAULT : Colors.background.primary,
              borderColor: on ? Colors.accent.DEFAULT : Colors.border.default,
              color: on ? Colors.text.inverse : Colors.text.secondary,
            }}
          >
            {option === 'ours' ? 'Ours' : 'Hired'}
          </button>
        );
      })}
    </div>
  );
}

function PickRow({ picked, name, meta, onPick, testID }) {
  const Icon = picked ? CircleDot : Circle;
  return (
    <button
      type="button"
      data-testid={testID}
      onClick={onPick}
      className="flex w-full items-center gap-3 rounded-[10px] px-1 py-2.5 text-left"
      style={{ backgroundColor: picked ? Colors.accent.wash : 'transparent' }}
    >
      <Icon
        className="h-5 w-5 shrink-0"
        style={{ color: picked ? Colors.accent.DEFAULT : Colors.text.disabled }}
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-semibold" style={{ color: Colors.text.primary }}>{name}</span>
        <span className="block truncate text-[11px]" style={{ color: Colors.text.secondary }}>{meta}</span>
      </span>
    </button>
  );
}

export default function DispatchLoadSheet({
  open, onClose, summary, vehicles, drivers, submitting, onSubmit, onAddVehicle, onAddDriver,
}) {
  const [vehicleMode, setVehicleMode] = useState('ours');
  const [driverMode, setDriverMode] = useState('ours');
  const [vehicleId, setVehicleId] = useState('');
  const [driverId, setDriverId] = useState('');
  const [hiredVehicle, setHiredVehicle] = useState({ number: '', type: '' });
  const [hiredDriver, setHiredDriver] = useState({ name: '', mobile: '' });
  const [adding, setAdding] = useState(null);
  const [newVehicle, setNewVehicle] = useState({ number: '', type: '' });
  const [newDriver, setNewDriver] = useState({ name: '', mobile: '' });
  const [savingNew, setSavingNew] = useState(false);
  const [error, setError] = useState('');

  // one van free and one man free is the ordinary evening — picking them by hand
  // is a tap spent on a decision that has already been made
  useEffect(() => {
    if (!open) return;
    setError('');
    setAdding(null);
    if (vehicles.length === 1) setVehicleId((prev) => prev || vehicles[0].id);
    if (drivers.length === 1) setDriverId((prev) => prev || drivers[0].id);
  }, [open, vehicles, drivers]);

  const choice = useMemo(() => {
    const vehicle = vehicles.find((v) => v.id === vehicleId);
    const driver = drivers.find((d) => d.id === driverId);
    const vehicleNumber = vehicleMode === 'ours' ? vehicle?.vehicle_number || '' : hiredVehicle.number.trim();
    const driverName = driverMode === 'ours' ? driver?.name || '' : hiredDriver.name.trim();
    const driverMobile = driverMode === 'ours' ? driver?.mobile_number || '' : hiredDriver.mobile.trim();
    if (!vehicleNumber || !driverName) return null;
    return {
      vehicle_id: vehicleMode === 'ours' ? vehicleId : null,
      vehicle_number: vehicleNumber,
      vehicle_type: vehicleMode === 'ours' ? vehicle?.vehicle_type || '' : hiredVehicle.type.trim(),
      is_external_vehicle: vehicleMode === 'hired',
      driver_id: driverMode === 'ours' ? driverId : null,
      driver_name: driverName,
      driver_mobile: driverMobile,
      is_external_driver: driverMode === 'hired',
    };
  }, [vehicles, drivers, vehicleId, driverId, vehicleMode, driverMode, hiredVehicle, hiredDriver]);

  const submit = () => {
    if (!choice) {
      setError(vehicleMode === 'hired' && !hiredVehicle.number.trim()
        ? 'Type the vehicle number'
        : 'Pick a vehicle and a driver');
      return;
    }
    if (driverMode === 'hired' && !isMobileNumber(choice.driver_mobile)) {
      setError('A hired driver needs a 10-digit mobile number');
      return;
    }
    setError('');
    onSubmit(choice);
  };

  const saveVehicle = async () => {
    if (!newVehicle.number.trim()) { setError('Type the vehicle number'); return; }
    setSavingNew(true);
    const saved = await onAddVehicle({
      vehicle_number: newVehicle.number.trim(),
      vehicle_type: newVehicle.type.trim() || 'Truck',
    });
    setSavingNew(false);
    if (saved) {
      setVehicleId(saved.id);
      setNewVehicle({ number: '', type: '' });
      setAdding(null);
      setError('');
    }
  };

  const saveDriver = async () => {
    if (!newDriver.name.trim()) { setError('Type the driver name'); return; }
    if (!isMobileNumber(newDriver.mobile)) { setError('A driver needs a 10-digit mobile number'); return; }
    setSavingNew(true);
    const saved = await onAddDriver({
      name: newDriver.name.trim(),
      mobile_number: newDriver.mobile.trim(),
    });
    setSavingNew(false);
    if (saved) {
      setDriverId(saved.id);
      setNewDriver({ name: '', mobile: '' });
      setAdding(null);
      setError('');
    }
  };

  const sectionHead = (label, mode, setMode, testID) => (
    <div className="mb-2 flex items-center justify-between">
      <span className="text-[10px] font-medium tracking-[1.2px]" style={{ color: Colors.text.tertiary }}>{label}</span>
      <ModeToggle mode={mode} onChange={(m) => { setMode(m); setAdding(null); }} testID={testID} />
    </div>
  );

  const addActions = (onCancel, onSave, label, saveTestID) => (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={onCancel}
        className="flex-1 rounded-[10px] border py-2.5 text-[12px] font-medium"
        style={{ borderColor: Colors.border.default, color: Colors.text.secondary }}
      >
        Cancel
      </button>
      <button
        type="button"
        data-testid={saveTestID}
        onClick={onSave}
        disabled={savingNew}
        className="flex flex-1 items-center justify-center rounded-[10px] py-2.5 text-[12px] font-bold disabled:opacity-60"
        style={{ backgroundColor: Colors.accent.DEFAULT, color: Colors.text.inverse }}
      >
        {savingNew ? <Spinner size="sm" className="!border-white !border-b-transparent" /> : label}
      </button>
    </div>
  );

  return (
    <BottomSheet open={open} onClose={onClose} title="Send it out" subtitle={summary} testID="load-sheet">
      <div className="max-h-[26rem] overflow-y-auto pt-1">
        {sectionHead('VEHICLE', vehicleMode, setVehicleMode, 'vehicle-mode')}

        {vehicleMode === 'ours' ? (
          adding === 'vehicle' ? (
            <div className="flex flex-col gap-2">
              <Field
                testID="new-vehicle-number"
                value={newVehicle.number}
                onChange={(v) => setNewVehicle((p) => ({ ...p, number: v }))}
                placeholder="Vehicle number"
                autoCapitalize="characters"
              />
              <Field
                testID="new-vehicle-type"
                value={newVehicle.type}
                onChange={(v) => setNewVehicle((p) => ({ ...p, type: v }))}
                placeholder="Type — Truck, Tempo, Van"
              />
              {addActions(() => setAdding(null), saveVehicle, 'Add vehicle', 'save-new-vehicle')}
            </div>
          ) : (
            <div>
              {vehicles.length === 0 && (
                <p className="py-2 text-[12px]" style={{ color: Colors.text.tertiary }}>
                  No vehicle is free. Add one, or hire.
                </p>
              )}
              {vehicles.map((vehicle) => (
                <PickRow
                  key={vehicle.id}
                  testID={`pick-vehicle-${vehicle.id}`}
                  picked={vehicleId === vehicle.id}
                  name={vehicle.vehicle_number}
                  meta={`${vehicle.vehicle_type || 'Vehicle'}${vehicle.capacity ? ` · ${vehicle.capacity}` : ''}`}
                  onPick={() => setVehicleId(vehicle.id)}
                />
              ))}
              <button
                type="button"
                data-testid="add-vehicle-btn"
                onClick={() => { setAdding('vehicle'); setError(''); }}
                className="flex items-center gap-1.5 py-2 text-[12px] font-semibold"
                style={{ color: Colors.accent.DEFAULT }}
              >
                <PlusCircle className="h-4 w-4" /> Add a vehicle
              </button>
            </div>
          )
        ) : (
          <div className="flex flex-col gap-2">
            <Field
              testID="hired-vehicle-number"
              value={hiredVehicle.number}
              onChange={(v) => setHiredVehicle((p) => ({ ...p, number: v }))}
              placeholder="Vehicle number"
              autoCapitalize="characters"
            />
            <Field
              testID="hired-vehicle-type"
              value={hiredVehicle.type}
              onChange={(v) => setHiredVehicle((p) => ({ ...p, type: v }))}
              placeholder="Type — Truck, Tempo, Van"
            />
          </div>
        )}

        <div className="mt-5">{sectionHead('DRIVER', driverMode, setDriverMode, 'driver-mode')}</div>

        {driverMode === 'ours' ? (
          adding === 'driver' ? (
            <div className="flex flex-col gap-2">
              <Field
                testID="new-driver-name"
                value={newDriver.name}
                onChange={(v) => setNewDriver((p) => ({ ...p, name: v }))}
                placeholder="Driver name"
              />
              <Field
                testID="new-driver-mobile"
                value={newDriver.mobile}
                onChange={(v) => setNewDriver((p) => ({ ...p, mobile: v }))}
                placeholder="Mobile number"
                inputMode="tel"
              />
              {addActions(() => setAdding(null), saveDriver, 'Add driver', 'save-new-driver')}
            </div>
          ) : (
            <div>
              {drivers.length === 0 && (
                <p className="py-2 text-[12px]" style={{ color: Colors.text.tertiary }}>
                  No driver is free. Add one, or hire.
                </p>
              )}
              {drivers.map((driver) => (
                <PickRow
                  key={driver.id}
                  testID={`pick-driver-${driver.id}`}
                  picked={driverId === driver.id}
                  name={driver.name}
                  meta={driver.mobile_number || 'No number on file'}
                  onPick={() => setDriverId(driver.id)}
                />
              ))}
              <button
                type="button"
                data-testid="add-driver-btn"
                onClick={() => { setAdding('driver'); setError(''); }}
                className="flex items-center gap-1.5 py-2 text-[12px] font-semibold"
                style={{ color: Colors.accent.DEFAULT }}
              >
                <PlusCircle className="h-4 w-4" /> Add a driver
              </button>
            </div>
          )
        ) : (
          <div className="flex flex-col gap-2">
            <Field
              testID="hired-driver-name"
              value={hiredDriver.name}
              onChange={(v) => setHiredDriver((p) => ({ ...p, name: v }))}
              placeholder="Driver name"
            />
            <Field
              testID="hired-driver-mobile"
              value={hiredDriver.mobile}
              onChange={(v) => setHiredDriver((p) => ({ ...p, mobile: v }))}
              placeholder="Mobile number"
              inputMode="tel"
            />
          </div>
        )}
      </div>

      {!!error && (
        <p data-testid="load-sheet-error" className="mt-3 text-[12px] font-medium" style={{ color: Colors.status.error }}>
          {error}
        </p>
      )}

      <button
        type="button"
        data-testid="confirm-load"
        onClick={submit}
        disabled={submitting}
        className="mt-4 flex h-12 w-full items-center justify-center rounded-[10px] text-[15px] font-bold disabled:opacity-60"
        style={{ backgroundColor: Colors.accent.DEFAULT, color: Colors.text.inverse }}
      >
        {submitting ? <Spinner size="sm" className="!border-white !border-b-transparent" /> : 'Book the trip'}
      </button>
    </BottomSheet>
  );
}
